import { RefObject } from 'react';

import { Icon, Spinfinity } from '@repo/ui';
import bgPlaceholder from 'assets/images/microbe-paper.svg';
import { ImageFallback, Typography } from 'components';
import VideoResource from 'types/VideoResource';

import { getYouTubeThumbnail } from '../VideoResources.helpers';
import styles from './FeaturedVideo.module.css';
import { ResourceTags } from './ResourceTags';
import shared from './Webinars.module.css';

interface FeaturedVideoProps {
  video: VideoResource | null;
  // The selected video is Tiny+ exclusive and the viewer isn't a member.
  isLocked: boolean;
  // React-owned wrapper the YouTube iframe is mounted into (see useYouTubePlayer).
  playerHostRef: RefObject<HTMLDivElement>;
  // Fired when the viewer clicks a locked thumbnail — opens the upsell.
  onUnlock: () => void;
}

export const FeaturedVideo = ({
  video,
  isLocked,
  playerHostRef,
  onUnlock,
}: FeaturedVideoProps) => {
  // Filter empty-states are owned by the container; a null video here means the
  // resource list is still loading.
  if (!video) return <Spinfinity />;

  return (
    <>
      {isLocked ? (
        <button
          type='button'
          className={styles.lockedThumbnail}
          onClick={onUnlock}
          aria-label={`Upgrade to Tiny+ to watch ${video.full_title}`}
        >
          <ImageFallback
            key={video.id}
            src={video.thumbnail_url}
            fallback={getYouTubeThumbnail(video.video_url) || bgPlaceholder}
            className={styles.imageFallback}
            alt={video.full_title}
          />
          <div className={styles.linearGradient} />
          <span className={styles.lockedPlayButton} aria-hidden='true'>
            <Icon name='playFilled' size={32} />
          </span>
          <span className={styles.lockedPill}>
            <Icon name='lockFilled' size='s' />
            <Typography variant='label'>Upgrade to Tiny+ to unlock</Typography>
          </span>
        </button>
      ) : (
        <div className={shared.videoEmbed}>
          {/* React owns this node; the YT iframe is mounted as a child of it. */}
          <div ref={playerHostRef} />
        </div>
      )}
      <div className={styles.featuredHeader}>
        <Typography variant='heading-m'>{video.full_title}</Typography>
        <ResourceTags tags={video.tags} />
      </div>
    </>
  );
};
